
import { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { testimonials, achievements } from "@/data/testimonials";

interface TestimonialsSectionProps {
  onContactClick: () => void;
}

const TestimonialsSection = ({ onContactClick }: TestimonialsSectionProps) => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      {
        threshold: 0.1,
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  // Sample ratings data based on HomeAdvisor format
  const ratingsData = {
    overall: 4.9,
    totalReviews: 59,
    distribution: [
      { stars: 5, count: 56 },
      { stars: 4, count: 2 },
      { stars: 3, count: 0 },
      { stars: 2, count: 1 },
      { stars: 1, count: 0 }
    ]
  };

  const sampleReviews = [
    {
      rating: 4.5,
      author: "John F.",
      location: "Elgin, SC",
      date: "1/24/2018",
      service: "Install or Replace Metal Stairs and Railings",
      text: "Swiss Welding LLC provided exceptional service for our metal stair installation. Their attention to detail and professional approach exceeded our expectations. The work was completed on time and within budget."
    },
    {
      rating: 4.0,
      author: "John F.",
      location: "Norwalk, CT",
      date: "1/19/2018",
      service: "Install or Replace Metal Stairs and Railings",
      text: "Outstanding craftsmanship on our custom railings. The team at Swiss Welding transformed our staircase into a work of art. Professional, reliable, and skilled welders."
    }
  ];

  return (
    <section
      id="testimonials"
      className={`py-20 bg-white section-reveal ${isVisible ? 'visible' : ''}`}
      ref={sectionRef}
    >
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <motion.h2
            className="text-3xl md:text-4xl font-montserrat font-bold text-primary mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            What Our Clients Say
          </motion.h2>
          <motion.p
            className="text-lg max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Verified reviews from our satisfied customers on HomeAdvisor
          </motion.p>
        </div>

        {/* Ratings Section */}
        <motion.div
          className="bg-[#f8f9fa] rounded-lg p-8 mb-12"
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <div className="grid md:grid-cols-2 gap-8">
            {/* Left Side - Overall Rating */}
            <div className="text-center">
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Ratings</h3>
              <div className="flex flex-col items-center">
                <div className="w-20 h-20 rounded-full border-4 border-[#D4AF37] flex items-center justify-center mb-4">
                  <span className="text-2xl font-bold text-[#D4AF37]">{ratingsData.overall}</span>
                </div>
                <div className="flex text-[#D4AF37] mb-2">
                  {[...Array(5)].map((_, i) => (
                    <svg
                      key={i}
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className="text-gray-600 font-semibold">{ratingsData.totalReviews} Verified Reviews</p>
              </div>
            </div>

            {/* Right Side - Rating Distribution */}
            <div>
              <div className="space-y-3">
                {ratingsData.distribution.map((item) => (
                  <div key={item.stars} className="flex items-center gap-3">
                    <span className="text-sm w-2">{item.stars}</span>
                    <svg className="w-4 h-4 text-[#D4AF37]" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    <div className="flex-1 bg-gray-200 rounded-full h-3">
                      <div 
                        className="bg-[#D4AF37] h-3 rounded-full"
                        style={{ width: `${(item.count / ratingsData.totalReviews) * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-sm text-gray-600 w-8">{item.count}</span>
                  </div>
                ))}
              </div>

              {/* Highly Rated For Section */}
              <div className="mt-6 p-4 bg-white rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                  Highly rated for
                  <svg className="w-4 h-4 ml-1 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </h4>
                <div className="space-y-2">
                  <div className="bg-gray-100 px-3 py-1 rounded text-sm">Overall Rating</div>
                  <div className="bg-gray-100 px-3 py-1 rounded text-sm">Value</div>
                  <div className="bg-gray-100 px-3 py-1 rounded text-sm">Customer Service</div>
                  <div className="bg-gray-100 px-3 py-1 rounded text-sm">Quality</div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Reviews Section */}
        <motion.div
          className="bg-[#f8f9fa] rounded-lg p-8"
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-2xl font-bold text-gray-800">Reviews</h3>
            <a 
              href="https://www.homeadvisor.com/rated.SwissWeldingLLC.50915952.html"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 font-medium transition-colors"
            >
              Write a Review
            </a>
          </div>

          <p className="text-gray-600 mb-6">Showing 41-50 of 59 results</p>

          <div className="space-y-6">
            {sampleReviews.map((review, index) => (
              <motion.div
                key={index}
                className="border-b border-gray-200 pb-6 last:border-b-0"
                initial={{ opacity: 0, y: 20 }}
                animate={isVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.6 + (index * 0.1) }}
              >
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-center gap-3">
                    <div className="flex text-[#D4AF37]">
                      {[...Array(Math.floor(review.rating))].map((_, i) => (
                        <svg key={i} className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>
                    <span className="font-semibold text-gray-800">{review.rating}</span>
                    <span className="font-semibold text-gray-800">{review.author}</span>
                    <span className="text-gray-600">{review.location}</span>
                  </div>
                  <span className="text-gray-500 text-sm">{review.date}</span>
                </div>
                <p className="text-blue-600 text-sm mb-2">{review.service}</p>
                <p className="text-gray-700">{review.text}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-8 text-center">
            <a 
              href="https://www.homeadvisor.com/rated.SwissWeldingLLC.50915952.html"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors"
            >
              View All Reviews on HomeAdvisor
              <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
        </motion.div>

        {/* Company Achievements Section */}
        <motion.div
          className="mt-20 border-t border-gray-200 pt-16"
          initial={{ opacity: 0, y: 20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <div className="text-center mb-12">
            <h3 className="text-2xl md:text-3xl font-montserrat font-bold text-primary mb-4">
              Our Achievements & Certifications
            </h3>
            <p className="text-lg text-[#555555] max-w-2xl mx-auto">
              Recognized for excellence in craftsmanship and customer service
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {achievements.map((achievement, index) => (
              <motion.div
                key={index}
                className="text-center"
                initial={{ opacity: 0, y: 30 }}
                animate={isVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.7, delay: 0.9 + (0.1 * index) }}
              >
                <div className="bg-white rounded-lg shadow-md p-4 hover:shadow-lg transition-shadow">
                  <div className="w-24 h-24 mx-auto mb-3 flex items-center justify-center">
                    {achievement.image ? (
                      <img
                        src={achievement.image}
                        alt={achievement.name}
                        className="w-full h-full object-contain"
                        loading="lazy"
                        onError={(e) => {
                          // Fallback to default icon if image fails to load
                          const target = e.currentTarget;
                          target.style.display = 'none';
                          const parent = target.parentElement;
                          if (parent) {
                            const fallbackDiv = parent.querySelector('.fallback-icon') as HTMLElement;
                            if (fallbackDiv) {
                              fallbackDiv.style.display = 'flex';
                            }
                          }
                        }}
                      />
                    ) : null}
                    <div 
                      className="fallback-icon w-20 h-20 bg-[#D4AF37] rounded-full flex items-center justify-center"
                      style={{ display: achievement.image ? 'none' : 'flex' }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-10 w-10 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                        />
                      </svg>
                    </div>
                  </div>
                  <h4 className="font-montserrat font-semibold text-primary mb-2 text-sm">
                    {achievement.name}
                  </h4>
                  <p className="text-xs text-[#555555]">
                    {achievement.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div
          className="mt-16 text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 1.0 }}
        >
          <p className="text-lg mb-6">Join our growing list of satisfied customers</p>
          <motion.button
            onClick={onContactClick}
            className="inline-block bg-[#D4AF37] hover:bg-primary text-white font-montserrat font-semibold py-3 px-10 rounded-md transition-colors shadow-lg"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Get a Free Quote
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
